import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClockCounterClockwise } from "@phosphor-icons/react";
import { EmptyState } from "../components/shared/EmptyState";
import { TeamLogo } from "../components/shared/TeamLogo";
import { getGames, getTeams } from "../lib/api";
import { LEAGUES } from "../constants/leagues";

const STATUS_LABEL = {
  scheduled: "Scheduled",
  in_progress: "Live",
  final: "Final",
  postponed: "Postponed",
  canceled: "Canceled",
};

const HistoricalGames = () => {
  const [league, setLeague] = useState("MLB");
  const [season, setSeason] = useState(String(new Date().getFullYear()));
  const [date, setDate] = useState("");
  const [teamId, setTeamId] = useState("");
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTeams({ league }).then(setTeams).catch(() => setTeams([]));
    setTeamId("");
  }, [league]);

  useEffect(() => {
    setLoading(true);
    const params = { league };
    if (date) params.date = date;
    else if (season) params.season = season;
    if (teamId) params.team_id = teamId;
    getGames(params)
      .then((data) => setGames(data || []))
      .catch(() => setGames([]))
      .finally(() => setLoading(false));
  }, [league, season, date, teamId]);

  return (
    <div data-testid="historical-games-page" className="mx-auto max-w-[1200px] px-4 py-8 sm:px-6">
      <div className="mb-6 flex items-center gap-2">
        <ClockCounterClockwise size={22} className="text-eb-accent" />
        <h1 className="font-heading text-3xl uppercase tracking-tight text-white">Game Explorer</h1>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-3">
        <div className="flex gap-1.5">
          {LEAGUES.map((lg) => (
            <button
              key={lg.code}
              data-testid={`explorer-league-${lg.code.toLowerCase()}`}
              onClick={() => setLeague(lg.code)}
              className={`rounded-md px-3 py-1.5 font-mono text-xs uppercase tracking-wide transition-colors ${
                league === lg.code
                  ? "border border-eb-accent bg-eb-accent/10 text-white"
                  : "border border-white/10 text-zinc-400 hover:border-white/30"
              }`}
            >
              {lg.code}
            </button>
          ))}
        </div>
        <input
          type="number"
          data-testid="explorer-season-input"
          value={season}
          onChange={(e) => {
            setSeason(e.target.value);
            setDate("");
          }}
          placeholder="Season"
          className="w-24 rounded-md border border-white/10 bg-eb-surface px-3 py-1.5 font-mono text-xs text-white"
        />
        <input
          type="date"
          data-testid="explorer-date-input"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded-md border border-white/10 bg-eb-surface px-3 py-1.5 font-mono text-xs text-white"
        />
        <select
          data-testid="explorer-team-select"
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
          className="rounded-md border border-white/10 bg-eb-surface px-3 py-1.5 font-mono text-xs uppercase text-white"
        >
          <option value="">All Teams</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="rounded-lg border border-white/10 bg-eb-surface p-10 text-center font-mono text-xs text-zinc-500">
          Loading games&hellip;
        </div>
      ) : games.length === 0 ? (
        <EmptyState icon={ClockCounterClockwise} title="No Games Found" description="Try a different league, season, date or team." testId="explorer-empty" />
      ) : (
        <div data-testid="explorer-results" className="flex flex-col gap-2">
          {games.map((g) => (
            <Link
              key={g.id}
              to={`/games/${g.id}`}
              data-testid={`explorer-game-${g.id}`}
              className="flex items-center justify-between rounded-lg border border-white/10 bg-eb-surface px-4 py-3 transition-colors hover:border-white/25"
            >
              <span className="flex items-center gap-2 font-heading text-sm text-white">
                <TeamLogo team={g.away_team} size={20} testId={`explorer-game-${g.id}-logo-away`} />
                {g.away_team?.name || g.away_team_id} @ {g.home_team?.name || g.home_team_id}
                <TeamLogo team={g.home_team} size={20} testId={`explorer-game-${g.id}-logo-home`} />
              </span>
              <span className="font-mono text-xs text-zinc-500">
                {g.game_date} &middot; {STATUS_LABEL[g.status] || g.status}
                {g.status === "final" ? ` \u00b7 ${g.away_score}-${g.home_score}` : ""}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistoricalGames;
